import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import { chatbotService } from '../../services/chatbotService';

// Generate or retrieve a stable visitor ID from localStorage
function getOrCreateVisitorId() {
  const KEY = 'adt_fatou_visitor_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

// Generate a session ID for this conversation session (resets on page reload)
function createSessionId() {
  return crypto.randomUUID();
}

const ChatWidget = () => {
  const [clientId, setClientId] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  const [sessionId] = useState(() => createSessionId());

  useEffect(() => {
    // Legacy clientId (kept for backward compat)
    let id = localStorage.getItem('fatou_client_id');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('fatou_client_id', id);
    }
    setClientId(id);

    // Stable visitor ID (persists across sessions)
    const vid = getOrCreateVisitorId();
    setVisitorId(vid);
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const focusInput = () => {
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 50);
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      const greeting = chatbotService?.getGreeting();
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isLoading) {
      focusInput();
    }
  }, [isOpen, isLoading, messages?.length]);

  const handleSendMessage = async () => {
    if (!inputValue?.trim() || isLoading) return;

    const userMessage = inputValue?.trim();
    setInputValue('');

    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    focusInput();

    try {
      const response = await chatbotService?.sendMessage(
        newMessages?.filter(msg => msg?.role !== 'system'),
        clientId,
        visitorId,
        sessionId,
      );

      setMessages([...newMessages, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Chat widget error:', error);
      const errorMessage = error?.message || "Désolée, je rencontre un problème technique. Veuillez réessayer dans un instant. 😔";
      setMessages([
        ...newMessages,
        { role: 'assistant', content: errorMessage }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Chat Button - Mobile Optimized */}
      <motion.button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-200 focus:outline-none focus:ring-4 focus:ring-green-300 overflow-hidden border-4 border-[#0b6b3a] bg-white touch-target"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Ouvrir le chat avec Fatou"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-[#0b6b3a] flex items-center justify-center"
            >
              <Icon name="X" size={24} className="text-white md:hidden" />
              <Icon name="X" size={28} className="text-white hidden md:block" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 0, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full"
            >
              <img 
                src="/assets/images/fatou_avatar_african_woman.png" 
                alt="Fatou - Assistante virtuelle ADT Express"
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
      {/* Chat Window - Mobile Responsive */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 md:bottom-28 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[90vw] md:max-w-md h-[calc(100vh-10rem)] sm:h-[600px] max-h-[70vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 md:p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
                  <img 
                    src="/assets/images/fatou_avatar_african_woman.png" 
                    alt="Fatou - Assistante virtuelle ADT Express"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-semibold text-base md:text-lg truncate">Fatou</h3>
                  <p className="text-white/80 text-xs truncate">Assistante virtuelle ADT</p>
                </div>
              </div>
              <button
                onClick={toggleChat}
                className="text-white/80 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10 flex-shrink-0 touch-target"
                aria-label="Fermer le chat"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Messages - Scrollable */}
            <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 bg-gray-50">
              {messages?.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${message?.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] md:max-w-[80%] rounded-2xl px-3 md:px-4 py-2 md:py-2.5 ${
                      message?.role === 'user' ?'bg-blue-600 text-white rounded-br-sm' :'bg-white text-gray-800 shadow-sm rounded-bl-sm border border-gray-100'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message?.content}</p>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-100">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input - Fixed at Bottom */}
            <div className="p-3 md:p-4 bg-white border-t border-gray-200 flex-shrink-0">
              <div className="flex items-end space-x-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e?.target?.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Posez votre question..."
                  rows={1}
                  className="flex-1 px-3 md:px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-base text-gray-900 placeholder-gray-500 bg-white min-h-[48px]"
                  disabled={isLoading}
                  style={{ fontSize: '16px' }}
                />
                <button
                  onClick={(e) => {
                    e?.currentTarget?.blur();
                    handleSendMessage();
                  }}
                  disabled={!inputValue?.trim() || isLoading}
                  className="bg-blue-600 text-white rounded-xl p-3 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 touch-target min-h-[48px] min-w-[48px] flex items-center justify-center"
                  aria-label="Envoyer le message"
                >
                  <Icon name="Send" size={20} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center leading-relaxed">
                Fatou est une IA et peut faire des erreurs. Vérifiez les informations importantes.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;